import type { ApplicationStatusLabelID, DealQuery } from '../api/application.types';
import type { RenewalStatusValues } from '../api/renewal.constants';

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

export type DrawerStatus =
  'closed' | 'editing' | 'error' | 'loading' | 'readonly' | 'submitted' | 'success' | 'denied';

export interface DrawerState {
  status: DrawerStatus;
  dealQuery: DealQuery | null;
  applicationStatusLabelId: ApplicationStatusLabelID | null;
  /** True when the drawer was opened from the Expiring Soon tab, whatever row depth was clicked. */
  openedFromExpiringSoon: boolean;
  /** Renewal status label id of the renewal-owning deal (drives the renewal banner). */
  renewalStatusLabelId: RenewalStatusValues | null;
  /** Deal owning the renewal opportunity — the primary tenant's deal when a co-tenant row was clicked. */
  renewalDealQuery: DealQuery | null;
  submissionManualUW: boolean | null;
  reset: () => void;
  isStatus: (status: DrawerStatus) => boolean;
  setDealData: (deal?: SetDealData) => void;
  setStatus: (status: DrawerStatus, submissionManualUW?: boolean) => void;
}

type SetDealData = {
  dealQuery?: DealQuery;
  applicationStatusLabelId?: ApplicationStatusLabelID;
  openedFromExpiringSoon?: boolean;
  renewalStatusLabelId?: RenewalStatusValues;
  renewalDealQuery?: DealQuery;
};

export const useDrawerStore = create<DrawerState>()(
  devtools(
    (set, get) => ({
      dealQuery: null,
      applicationStatusLabelId: null,
      openedFromExpiringSoon: false,
      renewalStatusLabelId: null,
      renewalDealQuery: null,
      submissionManualUW: null,
      setDealData: (deal) => {
        const {
          dealQuery,
          applicationStatusLabelId,
          openedFromExpiringSoon,
          renewalStatusLabelId,
          renewalDealQuery,
        } = deal || {};

        set({
          dealQuery: {
            id: dealQuery?.id,
            source: dealQuery?.source,
          },
          applicationStatusLabelId,
          openedFromExpiringSoon: openedFromExpiringSoon ?? false,
          renewalStatusLabelId: renewalStatusLabelId ?? null,
          renewalDealQuery: renewalDealQuery ?? null,
        });
      },
      isStatus: (status) => get().status === status,
      reset: () =>
        set({
          status: 'closed',
          dealQuery: null,
          applicationStatusLabelId: null,
          openedFromExpiringSoon: false,
          renewalStatusLabelId: null,
          renewalDealQuery: null,
          submissionManualUW: null,
        }),
      setStatus: (status, submissionManualUW) =>
        set({ status, submissionManualUW: submissionManualUW ?? null }),
      status: 'closed',
    }),
    {
      name: 'lease-details-store',
    },
  ),
);
